/* eslint-disable camelcase */
import { ProductInterface, ProductResponse } from '@westwing/appshell_types/PDPProduct';
import { ProductFormatter } from '@westwing/appshell-formatting-helpers';
import { Config } from '@westwing/appshell_types';
import { NormalizedFaqItem, NormalizedLandingPageData } from './types';
import { FAQBlock, FAQItem, HeroBlock, PromotionalPrice, ReviewsBlock } from '@ids/types/contentful';
import { Maybe } from '@ids/types/utility';
import { IDSLandingPageResponse, SerializedOurProjectsBlock } from '@ids/types/now-api';

export const getPromotionalPrice = (promotionalPrice: PromotionalPrice) => {
    if (
        promotionalPrice.startDate !== '' &&
        promotionalPrice.endDate !== '' &&
        promotionalPrice.price !== '<p><br></p>'
    ) {
        return promotionalPrice;
    }

    return null;
};

const normalizeHeroBlock = (heroBlock: HeroBlock): NormalizedLandingPageData['heroBlock'] => ({
    id: heroBlock.id,
    name: heroBlock.name,
    title: heroBlock.title,
    headline: heroBlock.headline,
    price: heroBlock.price,
    promotionalPrice: getPromotionalPrice(heroBlock.promotionalPrice),
    contentText: heroBlock.contentText,
    ctaText: heroBlock.ctaText,
    media: {
        desktop: heroBlock.idsMediaDesktop,
        mobile: heroBlock.idsMediaMobile,
    },
    textColor: heroBlock.textColor,
    textBackgroundColor: heroBlock.textBackgroundColor,
});

export const normalizeReviewsBlock = (reviewsBlock: ReviewsBlock): NormalizedLandingPageData['reviewsBlock'] => ({
    ...reviewsBlock,
    initialReviews: reviewsBlock?.initialReviews?.map(review => ({
        ...review,
        author: {
            is_anonymous: true,
            anonymous_name: '',
            username: '',
        },
        reply: null,
        original: null,
        rating: 5,
        image: null,
    })),
});

export const normalizeFaqBlockItem = (item: FAQItem): NormalizedFaqItem => ({
    question: item.questionText,
    answer: item.answer,
    id: item.id,
    name: item.name,
});

export const normalizeFaqBlock = (faqBlock: FAQBlock): NormalizedLandingPageData['faqBlock'] => ({
    id: faqBlock.id,
    name: faqBlock.name,
    title: faqBlock.title,
    faqItems: faqBlock.faqItems.map(normalizeFaqBlockItem),
});

const mapResponseToProducts = (products: ProductResponse[], config: Config): ProductInterface[] => {
    return products.map((product, index) =>
        ProductFormatter(index, product, undefined, undefined, {
            countryCode: config.countryCode,
            currency: config.currency,
        })
    );
};

export const normalizeOurProjectsBlock = (
    ourProjectsBlock: SerializedOurProjectsBlock,
    config: Config
): NormalizedLandingPageData['ourProjectsBlock'] => ({
    ...ourProjectsBlock,
    projects: ourProjectsBlock.projects.map(project => ({
        id: project.id,
        name: project.name,
        title: project.title,
        beforeImage: project.beforeImage,
        afterImage: project.afterImage,
        contentText: project.contentText,
        products: mapResponseToProducts(project.productsSkusResolved, config),
    })),
});

export default function normalizeLandingPageData(
    data: Maybe<IDSLandingPageResponse> | undefined,
    config: Config
): Maybe<NormalizedLandingPageData> {
    if (!data || !data?.data?.items?.length) {
        return null;
    }

    const idsPage = data.data.items[0];

    return {
        sku: idsPage.sku,
        heroBlock: normalizeHeroBlock(idsPage.heroBlock),
        ourProjectsBlock: normalizeOurProjectsBlock(idsPage.ourProjectsBlock, config),
        howItWorksBlock: idsPage.howItWorksBlock,
        whatYouGetBlock: idsPage.whatYouGetBlock,
        reviewsBlock: normalizeReviewsBlock(idsPage.reviewsBlock),
        faqBlock: normalizeFaqBlock(idsPage.faqBlock),
        pressBlock: idsPage.pressBlock,
        questionnaireFormId: idsPage.typeformId || undefined,
        projectSliderBlock: idsPage.projectSliderBlock,
    };
}
